/**
 * @fileoverview Outreach contact attribute type definitions and utilities.
 * Provides enums, formatters, and option generators for managing custom
 * contact attributes in the outreach module.
 * @module helperMethods/outreach/attribute
 */

import _ from "lodash";
import { formatCamelCaseToStringNormal, IOption } from ".";

/**
 * Supported attribute types for custom contact fields.
 */
export enum ATTRIBUTE_TYPE {
  TEXT_BOX = "textBox",
  TEXT_AREA = "textArea",
  DROPDOWN = "dropdown",
  CHECKBOX = "checkbox",
}

/**
 * Checkbox attribute option values.
 */
export enum OPTION_CHECKBOX {
  NONE = "None",
  TRUE = "True",
  FALSE = "False",
}

/**
 * Generates options for attribute type selection.
 *
 * @returns {IOption<ATTRIBUTE_TYPE>[]} Array of attribute type options
 *
 * @example
 * const types = attributeTypeOption();
 * // Returns: [{ label: 'Text Box', value: 'textBox' }, { label: 'Text Area', value: 'textArea' }, ...]
 */
export const attributeTypeOption = (): IOption<ATTRIBUTE_TYPE>[] =>
  Object.values(ATTRIBUTE_TYPE).map((value) => ({
    label: formatCamelCaseToStringNormal(value),
    value,
  }));

/**
 * Converts attribute name (underscore-separated) to display label (space-separated, capitalized).
 *
 * @param {string} [name] - Attribute name to convert
 * @returns {string} Formatted label, or empty string if name is undefined
 *
 * @example
 * convertNameToLabelAttribute('first_name');
 * // Returns: "First Name"
 *
 * @example
 * convertNameToLabelAttribute('custom_field_value');
 * // Returns: "Custom Field Value"
 */
export const convertNameToLabelAttribute = (name?: string): string => {
  if (!name) return "";
  return name
    .split("_")
    .map((text) => _.upperFirst(text))
    .join(" ");
};

/**
 * Generates checkbox options for attribute values.
 * Optionally includes 'None' option when creating new attributes.
 *
 * @param {boolean} [isCreateAttribute=false] - Whether this is for creating a new attribute
 * @returns {IOption<OPTION_CHECKBOX>[]} Array of checkbox value options
 *
 * @example
 * optionCheckbox(false);
 * // Returns: [{ label: 'True', value: 'True' }, { label: 'False', value: 'False' }]
 *
 * @example
 * optionCheckbox(true);
 * // Returns: [{ label: 'None', value: 'None' }, { label: 'True', value: 'True' }, { label: 'False', value: 'False' }]
 */
export const optionCheckbox = (isCreateAttribute = false): IOption<OPTION_CHECKBOX>[] => {
  let valueOption = Object.values(OPTION_CHECKBOX);
  if (!isCreateAttribute) {
    valueOption = valueOption.filter((value) => value !== OPTION_CHECKBOX.NONE);
  }
  return valueOption.map((value) => ({
    label: value,
    value,
  }));
};

/**
 * Converts an array of dropdown option strings to IOption format.
 *
 * @param {string[]} [options=[]] - Array of option strings
 * @returns {IOption<string>[]} Array of formatted option objects
 *
 * @example
 * optionDropdownAttribute(['Option A', 'Option B', 'Option C']);
 * // Returns: [
 * //   { label: 'Option A', value: 'Option A' },
 * //   { label: 'Option B', value: 'Option B' },
 * //   { label: 'Option C', value: 'Option C' }
 * // ]
 */
export const optionDropdownAttribute = (options: string[] = []): IOption<string>[] => {
  return options.map((label) => ({
    label,
    value: label,
  }));
};
